<template>
  <div>
    <operation-report-show-body-form-item-file
      v-if="isReady"
      v-bind="formData"
      @download="onDownload"
      @delete="onDelete"
      @upload="uploadTempFile"
      @change="onChange"
      @show-dialog="showUploadDialog"
      @close-dialog="closeUploadDialog"
    />
  </div>
</template>

<script>
import isString from "lodash/isString";
import OperationReportShowBodyFormItemFile from "./FormItemFile";
import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import operationReportService from "@/services/operations/operationReportService";
import {
  operationLoadingStart,
  operationLoadingClose,
  createStorageFilePath,
  getDataURLFromStoragePath,
  downloadImage,
  createOperationHash,
  notifyDownloadImageFailed,
  notifyUploadTempImageSuccess,
  notifyUploadTempImageFailed,
  calcDefaultValue,
  calcNextFormItemValue,
} from "@/utils/operations";
import AuthService from "@/services/authService";
import mapKeys from "lodash/mapKeys";
import isEmpty from "lodash/isEmpty";
import { createNamespacedHelpers } from "vuex";
const { mapActions } = createNamespacedHelpers("operationReport");

import { ColorMixin } from "@/mixins";
const { AISEA_COLOR } = ColorMixin.data();

const FALLBACK_VALUE = "ー";

// 12: SINGLE_IMAGE_FILE, 13: SINGLE_OTHER_FILE … 画像・ファイル両対応。拡張子でプレビュー／ファイル名表示を切り替え
export default {
  name: "FormItemFile",
  components: { OperationReportShowBodyFormItemFile },
  props: {
    reportId: {
      type: String,
      required: true,
    },

    // format
    enterpriseFormVersionItem: {
      type: Object,
      required: true,
    },

    reportFormVersion: {
      type: Object,
      required: true,
    },

    // 実データ
    reportFormVersionItem: {
      type: Object,
      required: true,
    },

    fallbackValue: {
      type: String,
      default: FALLBACK_VALUE,
    },

    // 編集権限
    updatable: {
      type: Boolean,
      default: false,
    },

    // 編集中かどうか
    isEditing: {
      type: Boolean,
      default: false,
    },
    // 書き込み先 ao_enterprise_vessel_report_form_version.id
    reportFormVersionId: {
      type: String,
      required: true,
    },
    // 可変行グループのファイルセルの場合の行番号。指定時はストレージパスに rows:{repeat_index}/ を付与し、
    // 保存値にも repeat_index を付ける（通常項目では null）。
    repeatIndex: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isReady: false,
      enterpriseId: "",
      fileAcquisitionResults: [], // ファイル表示用のデータ取得の結果
      isActiveUploadDialog: false, // ファイルアップロードダイアログ表示
    };
  },

  watch: {
    filePaths: {
      async handler() {
        if (!this.hasEnterpriseId) return;

        this.fileAcquisitionResults = await Promise.allSettled(
          this.filePaths.map(async filePath => {
            const { path, hash, tmpURL } = filePath;
            try {
              const dataURL = tmpURL
                ? tmpURL // 一時ファイル表示用のdataURL
                : await getDataURLFromStoragePath(path);
              return {
                hash,
                url: dataURL,
                success: true,
              };
            } catch {
              return {
                hash,
                url: "",
                success: false, // NOTE: データを取得できなかった場合も要素を使用したいので、全てresolveしてsuccessのフラグで判定
              };
            }
          })
        );
      },
      immediate: true,
      deep: true,
    },
  },

  computed: {
    name() {
      const {
        reportFormVersion,
        reportFormVersionItem,
        enterpriseFormVersionItem,
        updatable,
      } = this;
      const stateUpdatable = updatable ? "enable" : "disable";
      return `operation-report-form-${stateUpdatable}_${reportFormVersion.id}_${reportFormVersionItem.id}_${enterpriseFormVersionItem.id}`; // validationに使用
    },

    fileAcquisitionResultMap() {
      return mapKeys(
        this.fileAcquisitionResults.map(result => result.value),
        "hash"
      ); // {[key: hash]: {hash: string; url: string; success: boolean;}}
    },
    fileUrls() {
      // 要素のsuccessがtrueならファイルURL取得成功、falseなら失敗
      return this.filePaths.map(filePath => {
        const { hash } = filePath;
        const result = this.fileAcquisitionResultMap[hash];
        const loading = result === undefined; // NOTE: undefinedの場合はローディング中

        return {
          ...filePath,
          ...result, // {hash: string; url: string; success: boolean;}
          loading, // boolean
        };
      });
    },
    hasEnterpriseId() {
      return isString(this.enterpriseId) && this.enterpriseId.length > 0; // boolean
    },
    formDataForDisplay() {
      return {
        reportId: this.reportId,
        label: this.label,
        required: this.required,
        value: this.fileUrls,
        fallbackValue: this.nullableAndFallbackValue,
        isEditing: this.isEditing,
        name: this.name,
        nullableLabel: this.nullableLabel,
        isActiveUploadDialog: this.isActiveUploadDialog,
        disabled: this.disabled,
        isNullableChecked: this.isNullableChecked,
      };
    },
    disabled() {
      return !this.updatable;
    },
    formDataForEdit() {
      return { ...this.formDataForDisplay, defaultValue: this.defaultValue };
    },
    formData() {
      return this.isEditing ? this.formDataForEdit : this.formDataForDisplay;
    },
    isEdited() {
      return !isEmpty(this.reportFormVersionItem); // レポートのフォームの値がない時は、これまで編集していない
    },
    defaultValue() {
      const { enterpriseFormVersionItem, isEdited, disabled, isEditing } = this;
      if (disabled) return null; // 非活性ならデフォルト値を使用しない
      if (isEdited) return null; // すでに編集ずみの場合は、デフォルト値を使用しない
      if (!isEditing) return null; // 編集中でない場合は、デフォルト値を使用しない
      const defaultValue = calcDefaultValue(this, enterpriseFormVersionItem);
      if (defaultValue == null || !Array.isArray(defaultValue)) return null;
      return defaultValue.map(valueFile => {
        return {
          ...valueFile,
          isDefault: true, // デフォルト値の場合ファイルパスを作成方法が異なるため、フラグで見分ける
        };
      });
    },
    label() {
      const { enterpriseFormVersionItem } = this;
      return enterpriseFormService.getLabelFromEnterpriseFormVersionItem(
        enterpriseFormVersionItem
      );
    },
    required() {
      return enterpriseFormService.getRequiredFromEnterpriseFormVersionItem(
        this.enterpriseFormVersionItem
      );
    },
    filePaths() {
      const {
        valueFiles,
        calcFilePaths,
        calcFilePathsForDefaultValue,
        reportId,
      } = this;
      return valueFiles.map(valueFile => {
        const { isDefault } = valueFile;
        const path = isDefault
          ? calcFilePathsForDefaultValue(valueFile)
          : calcFilePaths(valueFile);
        return {
          path,
          hash: createOperationHash(`${reportId}-${path}`), // ファイルを一意に特定するhashを作成
          ...valueFile,
        };
      });
    },
    isNullableChecked() {
      return operationReportService.isNullableChecked(
        this.reportFormVersionItem
      ); // boolean
    },
    nullableLabel() {
      return enterpriseFormService.getNullableLabelFromEnterpriseFormVersionItem(
        this.enterpriseFormVersionItem
      ); // string
    },
    formVersionItemId() {
      return this.enterpriseFormVersionItem.id; // string
    },
    hasFile() {
      return operationReportService.hasReportFormVersionItemValue(
        this.reportFormVersionItem
      ); // boolean
    },
    valueFiles() {
      const { hasFile, calcFormValue, reportFormVersionItem } = this;
      if (!hasFile) return [];
      return calcFormValue(reportFormVersionItem); // {content_type: string; name: string; token: string; uploaded_at: string}[]
    },
    nullableAndFallbackValue() {
      if (this.isNullableChecked) return this.nullableLabel;
      return this.fallbackValue;
    },
  },

  methods: {
    ...mapActions({
      setFormVersionItemValue: "setFormVersionItemValue",
      uploadTempFilesOfCurrentFile: "uploadTempFilesOfCurrentFile",
    }),
    calcFormValue(reportFormVersionItem) {
      if (this.isNullableChecked) return this.nullableLabel;
      if (!this.hasFile) return this.fallbackValue;
      return operationReportService.getValueFromReportFormVersionItem(
        reportFormVersionItem
      );
    },

    // ストレージのファイルパスを作成
    calcFilePaths(valueFile) {
      const { enterpriseId, reportId, formVersionItemId, repeatIndex } = this;
      const { name } = valueFile;
      return createStorageFilePath(
        enterpriseId,
        reportId,
        formVersionItemId,
        name,
        repeatIndex
      ); // : string
    },

    // 値がデフォルトの場合のストレージのファイルパスを作成
    calcFilePathsForDefaultValue(valueFile) {
      const { enterpriseId, reportId, enterpriseFormVersionItem } = this;
      const formVersionItemId = enterpriseFormService.getDefaultValueId(
        enterpriseFormVersionItem
      );
      const { name } = valueFile;
      return createStorageFilePath(
        enterpriseId,
        reportId,
        formVersionItemId,
        name,
        this.repeatIndex
      ); // : string
    },

    async onDownload(
      file // {url: string; hash: string;}
    ) {
      try {
        operationLoadingStart(this);
        await downloadImage(file);
      } catch {
        notifyDownloadImageFailed(this);
      } finally {
        operationLoadingClose(this);
      }
    },

    onDelete() {
      const { reportFormVersionId, reportFormVersionItem } = this;
      const payload = {
        reportFormVersionId,
        ...reportFormVersionItem,
        value_files: [], // storeからファイルを削除 -> value_filesを[]にセットする
      };

      this.setFormVersionItemValue(payload);
    },

    async uploadTempFile(documents) {
      try {
        this.$vs.loading({ color: AISEA_COLOR.BLUE });
        const { src: tmpURL, content } = documents[0];
        const {
          formVersionItemId,
          reportFormVersionId,
          reportFormVersionItem,
          enterpriseFormVersionItem,
        } = this;

        const formData = new FormData();
        formData.append("file", content);

        const result = await this.uploadTempFilesOfCurrentFile({
          formData,
          formVersionItemId,
        });
        if (!result.status) {
          throw new Error(result.message);
        }
        notifyUploadTempImageSuccess(this);
        const nextFormItemValue = calcNextFormItemValue(
          enterpriseFormVersionItem,
          reportFormVersionItem,
          {
            value: [
              {
                ...result.data,
                tmpURL, // 一時ファイル表示用のdataURL
              },
            ],
          }
        );
        const payload = {
          reportFormVersionId,
          ...nextFormItemValue,
          // value_files: [result.data], // storeからファイルを削除 -> value_filesを[]にセットする
        };
        // 可変行グループのファイルセルは repeat_index を付与して該当行に保存する
        if (this.repeatIndex != null) {
          payload.repeat_index = this.repeatIndex;
        }

        this.setFormVersionItemValue(payload);
        this.closeUploadDialog();
      } catch {
        notifyUploadTempImageFailed(this);
      } finally {
        this.$nextTick(() => {
          this.$vs.loading.close();
        });
      }
    },

    async setEnterpriseId() {
      const user = await AuthService.getUser();
      this.enterpriseId = user.id;
    },

    async setup() {
      await this.setEnterpriseId();
      return true;
    },

    onChange(
      value // { value: [{ "name": string; }], nullableChecked }
    ) {
      const {
        reportFormVersionId,
        enterpriseFormVersionItem,
        reportFormVersionItem,
      } = this;
      const nextFormItemValue = calcNextFormItemValue(
        enterpriseFormVersionItem,
        reportFormVersionItem,
        value
      );

      const payload = {
        reportFormVersionId,
        ...nextFormItemValue,
      };

      this.setFormVersionItemValue(payload);
    },
    showUploadDialog() {
      this.isActiveUploadDialog = true;
    },
    closeUploadDialog() {
      setTimeout(() => {
        this.isActiveUploadDialog = false;
      });
    },
  },
  async created() {
    this.isReady = await this.setup();
  },
};
</script>
